import {pluralize} from 'BenchlingUtil';
import {
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {itemCountsForChangeSummary} from 'samples/getInventoryRegistryChangeSummary';

export interface TableSubmissionDiffs {
  // Update data used for the data modifications change summary modal. This is the diff between the current table values
  // and the state of the table if it had been resynced i.e. the current entity values.
  tableSubmissionUpdates: Array<StructuredTableDiff>;
}

export interface StructuredTableDiff {
  createdDate: Date;
  rowDiffs: Array<StructuredTableRowDiff>;
}

// TODO(BNCH-36124): Once processes are implemented, this exclusion can be removed
const UnsupportedStructuredTableKinds = ['PROCESS_OUTPUT', 'PROCESS_TASK', 'RUN_OUTPUT_SAMPLES'];

export function isStructuredTableUnsupported(structuredTables: Array<UnknownStructuredTable>): boolean {
  return structuredTables
    .map((structuredTable) => structuredTable.kind)
    .some((kind) => UnsupportedStructuredTableKinds.includes(kind));
}

interface StructuredTableRowDiff {
  rowId: string;
  isFinalized: boolean;
  cellDiffs: Array<StructuredTableCellDiff>;
}

export interface StructuredTableCellDiff {
  columnProvenanceUuid: string;
  oldValue: TypedValue | null;
  newValue: TypedValue | null;
}

/**
 * Generates the changes for the submitted structured tables
 * Used for the Data Modifications modal's change summary title.
 *
 * @param structuredTables - Array of structured tables being submitted
 * @param structuredTableAdapters - Corresponding adapters for each table
 * @param computedTables - Evaluated versions of each table
 * @param selection - Optional row range selection for partial submissions
 * @returns The diffs for the submitted structured tables
 */
export function getSubmittedModifications(
  structuredTables: Array<UnknownStructuredTable>,
  structuredTableAdapters: Array<UnknownStructuredTableAdapter>,
  computedTables: Array<EvaluatedTable>,
  selection?: {start: number; end: number}
): TableSubmissionDiffs {
  const tableSubmissionUpdates: Array<StructuredTableDiff> = [];

  for (let tableIndex = 0; tableIndex < structuredTables.length; tableIndex++) {
    const structuredTable = structuredTables[tableIndex];
    const adapter = structuredTableAdapters[tableIndex];
    const computedTable = computedTables[tableIndex];

    const rowDiffs: Array<StructuredTableRowDiff> = [];

    const cellUnsubmittedStatuses = adapter.getCellUnsubmittedStatuses(structuredTable, computedTable);

    for (let rowIndex = 0; rowIndex < structuredTable.rows.length; rowIndex++) {
      const structuredRow = structuredTable.rows[rowIndex];

      const isRowInSelection = !selection || (rowIndex >= selection.start && rowIndex < selection.end);
      if (!isRowInSelection) {
        continue;
      }

      if (!structuredRow.isFinalized) {
        continue;
      }

      const cellDiffs: Array<StructuredTableCellDiff> = [];
      const rowCellStatuses = cellUnsubmittedStatuses[rowIndex];
      for (let colIndex = 0; colIndex < rowCellStatuses.length; colIndex++) {
        if (rowCellStatuses[colIndex]) {
          cellDiffs.push({
            columnProvenanceUuid: structuredTable.columns[colIndex].provenanceUuid,
            oldValue: null,
            newValue: null,
          });
        }
      }

      if (cellDiffs.length > 0) {
        rowDiffs.push({
          rowId: structuredRow.rowId,
          isFinalized: true,
          cellDiffs,
        });
      }
    }

    tableSubmissionUpdates.push({
      createdDate: new Date(),
      rowDiffs,
    });
  }

  return {tableSubmissionUpdates};
}

export function structuredTablesContainFinalizedRow(
  structuredTables: Array<UnknownStructuredTable>
): boolean {
  return structuredTables
    .map((structuredTable) => structuredTable.rows.map((row) => row.isFinalized).includes(true))
    .includes(true);
}

export function getStructuredTableCellDiffCount(tableDiffs: Array<StructuredTableDiff>): number {
  return tableDiffs
    .map((tableDiff) => _getSingleTableCellDiffCount(tableDiff))
    .reduce((accumulator, diffCount) => accumulator + diffCount, 0);
}

function _getSingleTableCellDiffCount(tableDiff: StructuredTableDiff): number {
  return tableDiff.rowDiffs
    .map((row) => row.cellDiffs.length)
    .reduce((accumulator, diffCount) => accumulator + diffCount, 0);
}

export interface GetAddedRowCountOptions {
  selection?: {start: number; end: number};
}

export function getAddedRowCount(
  structuredTables: Array<UnknownStructuredTable>,
  options?: GetAddedRowCountOptions
): number {
  return structuredTables
    .map((table) => _getSingleTableAddedRowCount(table, options?.selection))
    .reduce((accumulator, count) => accumulator + count, 0);
}

function _getSingleTableAddedRowCount(
  structuredTable: UnknownStructuredTable,
  selection?: {start: number; end: number}
): number {
  return structuredTable.rows.filter((row, rowIndex) => {
    const isRowInSelection = !selection || (rowIndex >= selection.start && rowIndex < selection.end);
    return isRowInSelection && !row.isFinalized;
  }).length;
}

export function buildChangeSummary(itemChangeCount: {[k: string]: number}, addedRowCount: number): string {
  const counts = itemCountsForChangeSummary(itemChangeCount);
  return [
    counts ? `${counts} will be modified.` : '',
    addedRowCount > 0 ? `${addedRowCount} ${pluralize(addedRowCount, 'row')} will be added.` : '',
  ]
    .filter((summary) => summary.length > 0)
    .join(' ');
}
